import { Quote, QuoteRevision, formatBRL, formatDate, formatIsoDate } from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field } from '../../components/Form';
import { Drawer, Modal } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { API_BASE, api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { bpsInput, formatGrams, formatHm, parseBps } from '../../lib/numbers';
import { quotesKey, useInvalidateQuotes } from '../../lib/quotes-api';

/** "Sinal" card (Orcamento-Detalhe): ask for a deposit as a % of the total, then mark it paid. */
export function DepositCard({ quote }: { quote: Quote }) {
  const toast = useToast();
  const invalidate = useInvalidateQuotes();
  const deposit = quote.deposit;
  const [pct, setPct] = useState(bpsInput(deposit?.bps ?? 5000));
  const editable = ['draft', 'sent', 'accepted'].includes(quote.status);
  const patch = useMutation({
    mutationFn: (depositBps: number | null) =>
      api.patch(`/quotes/${quote.id}`, Quote, { depositBps }),
    onSuccess: () => invalidate(),
    onError: (err) => toast(errorMessage(err)),
  });
  const paid = useMutation({
    mutationFn: (value: boolean) =>
      api.post(`/quotes/${quote.id}/deposit/paid`, Quote, { paid: value }),
    onSuccess: (q) => {
      invalidate();
      toast(q.deposit?.paidAt ? 'Sinal marcado como recebido' : 'Sinal desmarcado');
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const bps = parseBps(pct);
  const valid = bps !== null && bps >= 1 && bps <= 10_000;
  const save = () => {
    if (valid && bps !== deposit?.bps) patch.mutate(bps);
  };
  return (
    <section className="card col" style={{ gap: 12 }}>
      <div className="row between">
        <h2>Sinal</h2>
        <button
          type="button"
          className={deposit ? 'toggle on' : 'toggle'}
          role="switch"
          aria-checked={!!deposit}
          aria-label="Pedir sinal"
          disabled={!editable || patch.isPending}
          onClick={() => patch.mutate(deposit ? null : valid ? bps : 5000)}
        />
      </div>
      {deposit ? (
        <div className="row wrap" style={{ gap: 16, alignItems: 'flex-end' }}>
          <div style={{ width: 140 }}>
            <Field id="dep-pct" label="Percentual">
              <AffixInput
                id="dep-pct"
                unit="%"
                value={pct}
                invalid={!valid}
                disabled={!editable}
                onChange={setPct}
              />
            </Field>
          </div>
          {editable && valid && bps !== deposit.bps ? (
            <button type="button" className="btn sm" onClick={save} disabled={patch.isPending}>
              Salvar
            </button>
          ) : null}
          <div className="field" style={{ width: 160 }}>
            <span className="lbl">Valor</span>
            <div className="input readonly">
              <span className="tab">{formatBRL(deposit.centavos)}</span>
            </div>
          </div>
          <button
            type="button"
            className="check"
            aria-pressed={!!deposit.paidAt}
            disabled={paid.isPending}
            onClick={() => paid.mutate(!deposit.paidAt)}
          >
            <span className={deposit.paidAt ? 'box on' : 'box'}>{deposit.paidAt ? '✓' : null}</span>
            Sinal pago
            {deposit.paidAt ? (
              <span className="xs muted">· {formatDate(deposit.paidAt)}</span>
            ) : null}
          </button>
        </div>
      ) : (
        <p className="sm muted">Sem sinal: a produção começa sem pagamento antecipado.</p>
      )}
    </section>
  );
}

/** "Revisões": the current one and the frozen earlier ones (open the read-only drawer). */
export function RevisionsCard({ quote }: { quote: Quote }) {
  const [open, setOpen] = useState<number | null>(null);
  if (quote.revisions.length === 0 && quote.revision === 1) return null;
  return (
    <section className="card col" style={{ gap: 4 }}>
      <h3 style={{ marginBottom: 6 }}>Revisões</h3>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start', padding: '8px 0' }}>
        <span className="badge solid">v{quote.revision}</span>
        <div className="grow">
          <p className="sm" style={{ fontWeight: 500 }}>
            Atual · {formatDate(quote.revisionAt)}
          </p>
          {quote.revisionNote ? <p className="xs muted">{quote.revisionNote}</p> : null}
        </div>
        <span className="tab sm">{formatBRL(quote.totals.priceCentavos)}</span>
      </div>
      {quote.revisions.map((r) => (
        <button
          key={r.revision}
          type="button"
          className="revbtn"
          style={{ opacity: 0.8 }}
          aria-label={`Ver itens da v${r.revision}`}
          onClick={() => setOpen(r.revision)}
        >
          <span className="badge">v{r.revision}</span>
          <span className="grow">
            <span className="sm" style={{ fontWeight: 500, display: 'block' }}>
              {formatDate(r.startedAt)}
            </span>
            <span className="xs muted">{r.note ?? 'Primeira proposta'}</span>
          </span>
          <span className="tab sm" style={{ textDecoration: 'line-through' }}>
            {formatBRL(r.totalCentavos)}
          </span>
        </button>
      ))}
      {open !== null ? (
        <RevisionDrawer quoteId={quote.id} revision={open} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}

function RevisionDrawer({
  quoteId,
  revision,
  onClose,
}: {
  quoteId: string;
  revision: number;
  onClose: () => void;
}) {
  const rev = useQuery({
    queryKey: [...quotesKey, quoteId, 'revision', revision],
    queryFn: () => api.get(`/quotes/${quoteId}/revisions/${revision}`, QuoteRevision),
  });
  const r = rev.data;
  return (
    <Drawer
      kicker="Somente leitura"
      title={r ? `v${r.revision} · ${formatDate(r.startedAt)}` : `v${revision}`}
      onClose={onClose}
    >
      {rev.isError ? <Alert tone="err">{errorMessage(rev.error)}</Alert> : null}
      {r ? (
        <>
          <p className="sm">{r.note ?? 'Primeira proposta'}</p>
          <div className="card flat">
            <div className="gt">
              <div className="gtr head" style={{ gridTemplateColumns: '1fr 44px 96px' }}>
                <span>Item</span>
                <span className="num">Qtd.</span>
                <span className="num">Preço</span>
              </div>
              {r.items.map((i, n) => (
                <div
                  key={`${i.description}-${n}`}
                  className="gtr readonly"
                  style={{ gridTemplateColumns: '1fr 44px 96px' }}
                >
                  <span>
                    <span style={{ display: 'block', fontWeight: 500, color: 'var(--ink)' }}>
                      {i.description}
                    </span>
                    <span className="xs">
                      {[i.materials.join(' + '), formatGrams(i.grams), formatHm(i.printSeconds)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="num">{i.quantity}</span>
                  <span className="num">{formatBRL(i.unitPriceCentavos * i.quantity)}</span>
                </div>
              ))}
              <div className="gtr foot" style={{ gridTemplateColumns: '1fr 44px 96px' }}>
                <span>Total</span>
                <span />
                <span className="num">{formatBRL(r.totalCentavos)}</span>
              </div>
            </div>
          </div>
          <p className="xs muted">
            Válido até {formatIsoDate(r.validUntil)}
            {r.sentAt ? ` · enviado em ${formatDate(r.sentAt)}` : ' · não enviado'}.
          </p>
          <p className="xs muted">
            Para voltar a esta versão, crie uma nova revisão e ajuste os itens.
          </p>
        </>
      ) : (
        <div className="skel" style={{ height: 200 }} />
      )}
    </Drawer>
  );
}

/** "Nova revisão · v3": freezes the current one and starts a draft. */
export function NewRevisionDialog({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateQuotes();
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const next = `v${quote.revision + 1}`;
  const create = useMutation({
    mutationFn: () => api.post(`/quotes/${quote.id}/revisions`, Quote, { note: note.trim() }),
    onSuccess: () => {
      invalidate();
      toast(`${next} criada como rascunho`);
      onClose();
    },
  });
  const empty = note.trim() === '';
  return (
    <Modal
      title={`Nova revisão · ${next}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={create.isPending}
            onClick={() => {
              setTried(true);
              if (!empty) create.mutate();
            }}
          >
            Criar {next}
          </button>
        </>
      }
    >
      <p className="sm muted">
        A v{quote.revision} fica guardada só para leitura. A nova revisão volta para Rascunho até
        você enviar.
      </p>
      <Field
        id="rvn"
        label="O que mudou?"
        error={tried && empty ? 'Escreva uma nota curta sobre a mudança.' : undefined}
      >
        <textarea
          id="rvn"
          className="input"
          placeholder="Ex.: troca para PLA Basic Dourado, base menor"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
    </Modal>
  );
}

/** "PDF para o cliente": preview, download and share (the PDF itself, via the phone's share sheet). */
export function PdfDialog({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const toast = useToast();
  const name = `${quote.number}-v${quote.revision}.pdf`;
  const url = `${API_BASE}/quotes/${quote.id}/pdf?v=${encodeURIComponent(quote.updatedAt)}`;
  const download = () => {
    const a = document.createElement('a');
    a.href = `${API_BASE}/quotes/${quote.id}/pdf?download=true`;
    a.download = name;
    a.click();
    toast(`PDF baixado: ${name}`);
  };
  const share = useMutation({
    mutationFn: async () => {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const file = new File([await res.blob()], name, { type: 'application/pdf' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `Orçamento ${quote.number}` });
        return true;
      }
      return false;
    },
    onSuccess: (shared) => {
      if (!shared) download();
    },
    onError: (err) => {
      // Closing the share sheet is not an error.
      if (!(err instanceof DOMException && err.name === 'AbortError')) toast(errorMessage(err));
    },
  });
  return (
    <Modal
      wide
      title="PDF para o cliente"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn"
            disabled={share.isPending}
            onClick={() => share.mutate()}
          >
            Compartilhar
          </button>
          <button type="button" className="btn p" onClick={download}>
            Baixar PDF
          </button>
        </>
      }
    >
      <object
        className="pdfframe"
        data={url}
        type="application/pdf"
        aria-label="Prévia do orçamento"
      >
        <p className="sm muted" style={{ padding: 16 }}>
          A prévia não abre neste aparelho. Use “Baixar PDF” ou “Compartilhar”.
        </p>
      </object>
      <p className="xs muted">
        Sai com o logo, os dados do negócio, os itens com preço (sem custos), a validade, o prazo e
        o sinal. A chave Pix entra quando estiver em Configurações › Negócio.
      </p>
    </Modal>
  );
}
